import { useContainer } from "features/container/Feature";
import { useEditor } from "features/editor/Feature";
import { PreviewProvider } from "features/preview/Feature";
import type { UnassignedPortTab } from "features/types/tabs";
import { useSetupProgress } from "hooks/pitcher/useSetupProgress";
import { useUnassignedPorts } from "hooks/pitcher/useUnassignedPorts";
import type { InjectFunction } from "preview-protocol";

import { withPitcherUrlProtocol } from "utils/url";
import { executeVSCodeCommand } from "utils/vscode";

import { Preview } from "./Preview/Preview";
import type {
  MessageFromDevToolToPreview,
  MessageFromPreviewToDevTool,
} from "./Preview/types";
import type { TabPanelProps } from "./types";

// This function is injected into the Preview iframe
// and is used to set the preview secret
const SandboxSecretInjection: InjectFunction<
  MessageFromDevToolToPreview,
  MessageFromPreviewToDevTool
> = ({ previewWindow, scope }) => {
  const PREVIEW_SECRET_COOKIE_NAME = "csb_sandbox_secret";
  const secret = scope.secret;

  const existingPreviewSecret = document.cookie.replace(
    new RegExp(
      `(?:(?:^|.*;\\s*)${PREVIEW_SECRET_COOKIE_NAME}\\s*\\=\\s*([^;]*).*$)|^.*$`,
    ),
    "$1",
  );

  if (secret && existingPreviewSecret !== secret) {
    previewWindow.document.cookie = `${PREVIEW_SECRET_COOKIE_NAME}=${secret};samesite=none;secure;partitioned;`;
    previewWindow.location.reload();
  } else if (!secret && existingPreviewSecret) {
    previewWindow.document.cookie = `${PREVIEW_SECRET_COOKIE_NAME}=;samesite=none;secure;expires=Thu, 01 Jan 1970 00:00:01 GMT;partitioned;`;
  }
};

export const UnassignedPortDevtool: React.FC<
  TabPanelProps<UnassignedPortTab>
> = ({ tab, panelId }) => {
  const { id: tabId, port, path: initialPath } = tab;
  const [container] = useContainer();
  const setupProgress = useSetupProgress();
  const editor = useEditor();

  const unassignedPorts = useUnassignedPorts();
  const unassignedPort =
    container.state !== "RESTARTING" &&
    setupProgress.state !== "IN_PROGRESS" &&
    unassignedPorts.find((p) => p.port === port);
  const isBrowserSandbox = editor.type === "sandbox" && !editor.isCloud;
  const browserPreviewSecret = isBrowserSandbox ? editor.previewSecret : null;

  return (
    <PreviewProvider
      // Since the preview can be Sandpack we also add the id of the Sandbox to remount the preview if we seamlessly branch
      key={
        tabId +
        editor.id +
        (unassignedPort ? unassignedPort.url : "") +
        // Since the preview can be Sandpack we also add the id of the Sandbox to remount the preview if we seamlessly branch. Also
        // we need to remount of the privacy changes
        String(editor.isPrivate)
      }
      devtoolId={tabId}
      initialPath={initialPath}
      origin={unassignedPort ? withPitcherUrlProtocol(unassignedPort.url) : ""}
    >
      <Preview
        canRestartProcess={false}
        disablePointerEvents={false}
        isPortActive={!!unassignedPort}
        isPrimaryPort={isBrowserSandbox}
        onConnected={(injectAndInvoke) => {
          injectAndInvoke(SandboxSecretInjection, {
            secret: browserPreviewSecret,
          });
        }}
        onKillAction={() => {
          executeVSCodeCommand("codesandbox.layout.closeDevtool", {
            type: "UNASSIGNED_PORT",
            port,
          });
        }}
        onMountIframe={(iframe) => {
          if (
            isBrowserSandbox &&
            unassignedPort &&
            unassignedPort.mountSandpack
          ) {
            return unassignedPort.mountSandpack(iframe);
          }

          return Promise.resolve(null);
        }}
        panelId={panelId}
        shell={null}
        tab={tab}
      />
    </PreviewProvider>
  );
};
